import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

// Match the shopping bag's parent/extra grouping without changing chargeable lines.
function medicalCartItemCount(cart) {
  const items = cart.items;
  const parentKey = (item) => item.parent_relationship?.parent_key || item.parent_relationship?.parent?.key || '';
  const parents = items.filter((item) => !item.properties?._med_extra && !parentKey(item));
  return items.reduce((count, item) => {
    const owner = parents.find((parent) =>
      (parentKey(item) && parentKey(item) === parent.key) ||
      (item.properties?._med_extra && item.properties?._med_bundle &&
        item.properties._med_bundle === parent.properties?._med_bundle)
    );
    return count + (owner ? 0 : Number(item.quantity) || 0);
  }, 0);
}

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 * @property {HTMLElement} cartBubbleAccessibleCount - Count announced by screen readers.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];
  countRequest = 0;

  /** @type {number} */
  get currentCartCount() {
    return Number(this.refs.cartBubbleCount.dataset.medCartCount) || 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.dataset.medCartCount = String(value);
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
    if (this.refs.cartBubbleAccessibleCount) this.refs.cartBubbleAccessibleCount.textContent = String(value);
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.countRequest++;

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.refreshCartCount();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    if (event.detail.data?.didError) return;
    await this.refreshCartCount(event.detail.resource);
  };

  refreshCartCount = async (cart) => {
    const request = ++this.countRequest;
    try {
      // Add responses can contain only the new lines. Use a complete saved cart.
      if (!Array.isArray(cart?.items) || typeof cart.item_count !== 'number') {
        const response = await fetch(`${this.dataset.cartUrl}.js`, {
          credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;
        cart = await response.json();
      }
      if (!Array.isArray(cart?.items) || request !== this.countRequest || !this.isConnected) return;
      await this.renderCartBubble(medicalCartItemCount(cart));
    } catch (_) {
      // Retain the last valid count if the read fails; never alter the cart itself.
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} animate - Whether to animate the updated count.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;
    this.refs.cartBubble.toggleAttribute('data-maintain-ratio', itemCount <= 99);

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    if (!this.refs.cartBubbleCount) return;
    // Fresh Liquid markup is authoritative; old cached counts include add-ons.
    this.renderCartBubble(this.currentCartCount, false);
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
